import React, { useMemo, useState } from "react";
import { Box, IconButton, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, Tooltip, Popover, Card, CardContent, Divider } from "@mui/material";
import { ChevronLeft, ChevronRight, Today, Close } from "@mui/icons-material";
import { METALS_CALENDAR_COLORS, ESTADO_ORO_COLORS } from "../../style/colors.js";
import { 
  toDateKey, 
  normalizeDailyNeeds, 
  calculateDayAggregates, 
  calculateDimensions,
  calculateDailyAvailability
} from "../../services/utils/calendarUtils.js";

export default function ComponentMetalsCalendar({ 
  baseStock, 
  details, 
  excelData,
  StockTotalOro,
  StockTotalPlatino,
  title = "Calendario de Metales", 
  compact = false, 
  size = "md", 
  fixedHeight, 
  showTitle = true, 
  cellWidth,
  provisionFactor = 1,
  sideDetails = false,
  onOrderClick
}) {
  const today = new Date();
  const [cursor, setCursor] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [selectedDay, setSelectedDay] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [calendarRef, setCalendarRef] = useState(null);

  const { dims } = calculateDimensions(size, compact, fixedHeight, showTitle, cellWidth);

  const dailyNeeds = useMemo(() => normalizeDailyNeeds(details), [details]);

  const dayAggregates = useMemo(() => {
    if (excelData && excelData.length > 0 && StockTotalOro && StockTotalPlatino) {
      return calculateDailyAvailability(cursor, StockTotalOro, StockTotalPlatino, excelData, provisionFactor);
    } else {
      return calculateDayAggregates(cursor, baseStock, dailyNeeds);
    }
  }, [cursor, StockTotalOro, StockTotalPlatino, excelData, baseStock, dailyNeeds, provisionFactor]);

  const headerMonth = cursor.toLocaleDateString("es-ES", { month: "long", year: "numeric" }).replace(/^\w/, c => c.toUpperCase());
  const { matrix, data: dataMap } = dayAggregates;

  const goPrev = () => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1));
  const goNext = () => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1));
  const goToday = () => setCursor(new Date(today.getFullYear(), today.getMonth(), 1));

  const isToday = (date) => toDateKey(date) === toDateKey(today);
  const inCurrentMonth = (date) => date.getMonth() === cursor.getMonth();

  const excelSerialToDate = (serial) => {
    if (!serial || isNaN(serial)) return null;
    const excelEpoch = new Date("1899-12-30T00:00:00Z");
    return new Date(excelEpoch.getTime() + serial * 24 * 60 * 60 * 1000);
  };

  // Pedidos (MO) cuya fecha de entrega es exactamente ese día
  const getOrdersForDate = (date) => {
    if (!date || !Array.isArray(excelData)) return [];
    return excelData.filter((item) => {
      const d = excelSerialToDate(item.fecha_entrega);
      return (
        d &&
        d.getFullYear() === date.getFullYear() &&
        d.getMonth() === date.getMonth() &&
        d.getDate() === date.getDate()
      );
    });
  };

  // Construye el objeto de detalle de un día a partir del mapa de agregados
  const buildDayInfo = (date) => {
    if (!date) return null;
    const info = dataMap[toDateKey(date)];
    if (!info || info.oroDisponible === null || info.oroDisponible === undefined) return null;
    return {
      date,
      oroDisponible: info.oroDisponible,
      platinoDisponible: info.platinoDisponible,
      requeridoOro: info.requeridoOro || 0,
      requeridoPlatino: info.requeridoPlatino || 0,
      stockTotalOro: info.stockTotalOro ?? StockTotalOro,
      stockTotalPlatino: info.stockTotalPlatino ?? StockTotalPlatino,
    };
  };

  const WeekdayHeader = () => (
    <TableHead>
      <TableRow>
        {["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"].map((day, index) => (
          <TableCell key={day} sx={{
            backgroundColor: "#f8fafc",
            color: index >= 5 ? "#94a3b8" : "#64748b",
            fontWeight: 600,
            fontSize: "0.66rem",
            letterSpacing: "0.3px",
            textTransform: "uppercase",
            textAlign: "center",
            border: `1px solid ${METALS_CALENDAR_COLORS.DAY_BORDER}`,
            px: 0.5,
            py: 0.6,
          }}>
            {day}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );

  const DayCell = ({ date }) => {
    const key = toDateKey(date);
    const info = dataMap[key];
    const muted = !inCurrentMonth(date);
    const borderStyle = isToday(date) 
      ? `2px solid ${METALS_CALENDAR_COLORS.TODAY_BORDER}` 
      : `1px solid ${METALS_CALENDAR_COLORS.DAY_BORDER}`;

    // Usar los nuevos datos si están disponibles
    const oroDisponible = info?.oroDisponible;
    const platinoDisponible = info?.platinoDisponible;
    const requeridoOro = info?.requeridoOro || 0;
    const requeridoPlatino = info?.requeridoPlatino || 0;

    // Determinar color de fondo según disponibilidad de oro
    const getBackgroundColor = () => {
      if (muted) return METALS_CALENDAR_COLORS.DAY_BG_ALT;
      if (oroDisponible === null) return METALS_CALENDAR_COLORS.DAY_BG; // Fondo blanco si no se calculó
      if (oroDisponible >= 0) return METALS_CALENDAR_COLORS.DAY_BG_POSITIVE;
      return METALS_CALENDAR_COLORS.DAY_BG_NEGATIVE;
    };

    const handleDayClick = (event) => {
      if (!muted && oroDisponible !== null) {
        setAnchorEl(calendarRef);
        setSelectedDay({
          date,
          oroDisponible,
          platinoDisponible,
          requeridoOro,
          requeridoPlatino,
          stockTotalOro: info?.stockTotalOro || StockTotalOro,
          stockTotalPlatino: info?.stockTotalPlatino || StockTotalPlatino
        });
      }
    };

    if (compact) {
      const clickable = !muted && oroDisponible !== null;
      const hasData = !muted && oroDisponible !== null;
      const isCover = hasData && oroDisponible >= 0 && platinoDisponible >= 0;
      const isDeficit = hasData && !isCover;
      const todayCell = isToday(date);
      const activeKey = toDateKey(selectedDay ? selectedDay.date : today);
      const isSelected = hasData && key === activeKey;

      const cellBg = muted
        ? "#f8fafc"
        : isSelected
          ? "#e3edfd"
          : todayCell
            ? "#fff8e1"
            : "#ffffff";

      // Barra de estado tipo "evento"
      const barBg = isCover ? "#e6f4ea" : "#fdecec";
      const barColor = isCover ? "#1e7e34" : "#c62828";

      return (
        <TableCell
          onClick={handleDayClick}
          sx={{
            verticalAlign: "top",
            p: 0,
            border: isSelected
              ? `2px solid ${METALS_CALENDAR_COLORS.TODAY_BORDER}`
              : `1px solid ${METALS_CALENDAR_COLORS.DAY_BORDER}`,
            boxShadow: isSelected ? `inset 0 0 0 1px ${METALS_CALENDAR_COLORS.TODAY_BORDER}` : "none",
            height: 42,
            backgroundColor: cellBg,
            cursor: clickable ? "pointer" : "default",
            transition: "background-color 0.12s ease",
            "&:hover": clickable ? { backgroundColor: isSelected ? "#d6e4fb" : "#eef2f7" } : {},
          }}
        >
          <Box sx={{ height: "100%", display: "flex", flexDirection: "column", p: 0.35, gap: 0.2 }}>
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: todayCell ? 700 : 500,
                  fontSize: "0.64rem",
                  lineHeight: 1,
                  color: muted ? "#cbd5e1" : todayCell ? "#b26a00" : "#475569",
                  ...(todayCell && {
                    backgroundColor: METALS_CALENDAR_COLORS.TODAY_BORDER,
                    color: "#ffffff",
                    borderRadius: "50%",
                    width: 15,
                    height: 15,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }),
                }}
              >
                {date.getDate()}
              </Typography>
            </Box>

            {hasData && (
              <Tooltip
                title={`Disponible — Oro: ${oroDisponible.toFixed(1)}g · Platino: ${platinoDisponible.toFixed(1)}g`}
                arrow
              >
                <Box
                  sx={{
                    mt: "auto",
                    backgroundColor: barBg,
                    color: barColor,
                    borderRadius: "3px",
                    px: 0.35,
                    py: 0.1,
                    fontSize: "0.68rem",
                    fontWeight: 700,
                    fontFamily: "monospace",
                    textAlign: "center",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {isCover ? "✓ cubre" : "déficit"}
                </Box>
              </Tooltip>
            )}
          </Box>
        </TableCell>
      );
    }

    return (
      <TableCell 
        onClick={handleDayClick}
        sx={{
          verticalAlign: "top",
          padding: 0.5,
          backgroundColor: getBackgroundColor(),
          border: borderStyle,
          height: 96,
          cursor: !muted && oroDisponible !== null ? "pointer" : "default",
          "&:hover": !muted && oroDisponible !== null ? {
            backgroundColor: oroDisponible >= 0 ? "#d4edda" : "#f8d7da",
            transform: "scale(1.02)"
          } : {}
        }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", px: 1, pt: 0.5 }}>
          <Typography variant="caption" sx={{ opacity: muted ? 0.5 : 1 }}>
            {date.getDate()}
          </Typography>
        </Box>

        <Box sx={{ px: 1, pb: 0.5, display: "flex", flexDirection: "column", gap: 0.25 }}>
          <Tooltip title={`Requerido Oro: ${requeridoOro.toFixed(1)}g | Platino: ${requeridoPlatino.toFixed(1)}g`} arrow>
            <Typography variant="caption" sx={{ color: METALS_CALENDAR_COLORS.NEED_TEXT, fontFamily: "monospace" }}>
              Req: {requeridoOro.toFixed(0)}g | {requeridoPlatino.toFixed(0)}g
            </Typography>
          </Tooltip>
          {oroDisponible !== null ? (
            <Tooltip title={`Disponible Oro: ${oroDisponible.toFixed(1)}g | Platino: ${platinoDisponible.toFixed(1)}g`} arrow>
              <Typography variant="caption" sx={{ 
                color: (oroDisponible >= 0 && platinoDisponible >= 0) 
                  ? METALS_CALENDAR_COLORS.AVAILABLE_POS_TEXT 
                  : METALS_CALENDAR_COLORS.AVAILABLE_NEG_TEXT, 
                fontFamily: "monospace" 
              }}>
                Disp: {oroDisponible.toFixed(0)}g | {platinoDisponible.toFixed(0)}g
              </Typography>
            </Tooltip>
          ) : (
            <Typography variant="caption" sx={{ color: METALS_CALENDAR_COLORS.TEXT_MUTED, fontFamily: "monospace" }}>
              Sin datos
            </Typography>
          )}
        </Box>
      </TableCell>
    );
  };

  const renderDayDetails = (dayData, { showClose = false } = {}) => {
    if (!dayData) {
      return (
        <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', color: 'text.secondary' }}>
          <Typography variant="caption" sx={{ fontSize: '0.72rem' }}>
            Selecciona un día del calendario para ver su detalle.
          </Typography>
        </Box>
      );
    }
    const ordersOfDay = getOrdersForDate(dayData.date);
    const dayIsToday = isToday(dayData.date);

    const statChip = (label, value, color) => (
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.4 }}>
        <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
          {label}
        </Typography>
        <Typography variant="caption" sx={{ fontFamily: 'monospace', fontWeight: 700, fontSize: '0.8rem', color }}>
          {value}g
        </Typography>
      </Box>
    );

    const metalRow = (label, stock, req, disp) => (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
        <Typography variant="caption" sx={{ width: 56, flexShrink: 0, fontWeight: 700, fontSize: '0.82rem', color: ESTADO_ORO_COLORS.SUMMARY_TEXT }}>
          {label}
        </Typography>
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {statChip('Stock', stock.toFixed(0), 'success.main')}
          {statChip('Req', req.toFixed(0), 'error.main')}
          {statChip('Disp', disp.toFixed(0), disp >= 0 ? 'success.main' : 'error.main')}
        </Box>
      </Box>
    );

    return (
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        {/* Cabecera fecha */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: ESTADO_ORO_COLORS.SUMMARY_TEXT, fontSize: '0.8rem', textTransform: 'capitalize' }}>
              {dayData.date.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' })}
            </Typography>
            {dayIsToday && (
              <Box sx={{ px: 0.75, py: 0.15, borderRadius: 1, backgroundColor: METALS_CALENDAR_COLORS.TODAY_BORDER, color: '#fff', fontSize: '0.58rem', fontWeight: 700, letterSpacing: '0.5px' }}>
                HOY
              </Box>
            )}
          </Box>
          {showClose && (
            <IconButton onClick={() => { setSelectedDay(null); setAnchorEl(null); }} size="small" sx={{ color: ESTADO_ORO_COLORS.SUMMARY_TEXT, p: 0.5 }}>
              <Close sx={{ fontSize: 16 }} />
            </IconButton>
          )}
        </Box>

        {/* Resumen Stock / Requerido / Disponible en 2 líneas (oro y platino) */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.35, pb: 0.75, borderBottom: `1px solid ${METALS_CALENDAR_COLORS.DAY_BORDER}` }}>
          {metalRow('Oro', dayData.stockTotalOro, dayData.requeridoOro, dayData.oroDisponible)}
          {metalRow('Platino', dayData.stockTotalPlatino, dayData.requeridoPlatino, dayData.platinoDisponible)}
        </Box>

        {/* Lista de pedidos (MO) — ocupa el resto con scroll */}
        <Typography variant="caption" sx={{ fontWeight: 'bold', color: ESTADO_ORO_COLORS.SUMMARY_TEXT, fontSize: '0.7rem', mt: 0.75, mb: 0.5 }}>
          Pedidos entregados este día ({ordersOfDay.length})
        </Typography>
        <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 0.5, pr: 0.5 }}>
          {ordersOfDay.length === 0 ? (
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.68rem', fontStyle: 'italic' }}>
              No hay entregas este día.
            </Typography>
          ) : (
            ordersOfDay.map((o, idx) => {
              const ordenValue = o.orden || o['1º_Ref'] || '';
              const isMo = /^[48]/.test(String(ordenValue).trim());
              const isClickable = isMo && typeof onOrderClick === 'function';
              return (
              <Box
                key={`${o.orden || o['1º_Ref'] || idx}-${idx}`}
                onClick={isClickable ? () => onOrderClick(ordenValue) : undefined}
                sx={{
                  border: '1px solid #eef0f2',
                  borderRadius: 1,
                  p: 0.5,
                  backgroundColor: '#fafbfc',
                  cursor: isClickable ? 'pointer' : 'default',
                  transition: 'background-color 0.12s ease, border-color 0.12s ease',
                  ...(isClickable && {
                    '&:hover': { backgroundColor: '#eef4ff', borderColor: '#b6ccf5' },
                  }),
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 0.5 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 700,
                      fontSize: '0.68rem',
                      color: isClickable ? '#1565c0' : ESTADO_ORO_COLORS.SUMMARY_TEXT,
                      textDecoration: isClickable ? 'underline' : 'none',
                    }}
                  >
                    {ordenValue || '—'}
                  </Typography>
                  <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.66rem', color: 'text.secondary' }}>
                    {parseFloat(o.gramos || 0).toFixed(1)}g
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 0.5 }}>
                  <Typography variant="caption" sx={{ fontSize: '0.64rem', color: 'text.secondary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {o.cliente || o.articulo || ''}
                  </Typography>
                  <Typography variant="caption" sx={{ fontSize: '0.62rem', color: '#8a6d3b', whiteSpace: 'nowrap' }}>
                    {o.metal || ''}
                  </Typography>
                </Box>
              </Box>
              );
            })
          )}
        </Box>
      </Box>
    );
  };

  const DayDetailsPopover = () => (
    <Popover
      open={selectedDay !== null}
      anchorEl={anchorEl}
      onClose={() => { setSelectedDay(null); setAnchorEl(null); }}
      anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
    >
      <Card sx={{ width: 320, borderRadius: 2 }}>
        <CardContent sx={{ p: 1.5 }}>
          {renderDayDetails(selectedDay, { showClose: true })}
        </CardContent>
      </Card>
    </Popover>
  );

  const activeDay = selectedDay || buildDayInfo(today);

  const calendarContent = (
    <>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
        {showTitle ? (
          <Typography variant={compact ? "body2" : "subtitle1"} sx={{ 
            fontWeight: "bold", 
            color: METALS_CALENDAR_COLORS.HEADER_TEXT, 
            backgroundColor: METALS_CALENDAR_COLORS.HEADER_BG, 
            px: 1, 
            py: dims.headerPx, 
            borderRadius: 1 
          }}>
            {title}
          </Typography>
        ) : (
          <Typography variant={compact ? "caption" : "subtitle2"} sx={{ fontWeight: 600, color: ESTADO_ORO_COLORS.SUMMARY_TEXT }}>
            {headerMonth}
          </Typography>
        )}
        
        <Box>
          <IconButton onClick={goPrev} size="small" sx={{ color: METALS_CALENDAR_COLORS.NAV_ICON }}>
            <ChevronLeft />
          </IconButton>
          <IconButton onClick={goToday} size="small" sx={{ 
            color: METALS_CALENDAR_COLORS.NAV_ICON, 
            padding: 0.25, 
            minWidth: "auto" 
          }}>
            <Today sx={{ fontSize: 18 }} />
          </IconButton>
          <IconButton onClick={goNext} size="small" sx={{ color: METALS_CALENDAR_COLORS.NAV_ICON }}>
            <ChevronRight />
          </IconButton>
        </Box>
      </Box>

      {showTitle && (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
          <Typography variant={compact ? "caption" : "subtitle2"} sx={{ fontWeight: 600 }}>
            {headerMonth}
          </Typography>
        </Box>
      )}

      <TableContainer>
        <Table size="small" sx={{ tableLayout: "fixed", width: "100%", borderCollapse: "collapse" }}>
          <WeekdayHeader />
          <TableBody>
            {matrix.map((row, i) => (
              <TableRow key={i}>
                {row.map((date) => (
                  <DayCell key={toDateKey(date)} date={date} />
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );

  // Modo con panel de detalle al lado (50% calendario / 50% detalle), altura fija
  if (sideDetails) {
    return (
      <Paper
        ref={setCalendarRef}
        elevation={0}
        sx={{ p: compact ? 0.5 : 1.5, backgroundColor: "transparent", width: "100%", height: 340, overflow: "hidden", marginBottom: 0 }}
      >
        <Box sx={{ display: "flex", gap: 2, alignItems: "stretch", height: "100%" }}>
          <Box sx={{ flex: "1 1 0", minWidth: 0, height: "100%", overflow: "hidden" }}>
            {calendarContent}
          </Box>
          <Box
            sx={{
              flex: "1 1 0",
              minWidth: 0,
              height: "100%",
              borderLeft: `1px solid ${METALS_CALENDAR_COLORS.DAY_BORDER}`,
              pl: 2,
            }}
          >
            {renderDayDetails(activeDay)}
          </Box>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper 
      ref={setCalendarRef}
      elevation={0} 
      sx={{ 
        p: compact ? 0.5 : 1.5, 
        backgroundColor: "transparent", 
        width: "100%", 
        height: fixedHeight ? fixedHeight : 'auto', 
        overflow: 'hidden', 
        marginBottom: 0
      }}>
      {calendarContent}
      <DayDetailsPopover />
    </Paper>
  );
}